import { useEffect, useRef, useState } from 'react';
import type { UseFormReturn } from 'react-hook-form';

import type {
    LocationProviderConfig,
    PenRef,
    ProviderGroup,
    ProviderSchema,
} from '@/domain/providerConfig';
import { penKey } from '@/routes/shared/ProviderConfig/form/formKeys';

import { type FieldConflict, mergeAfterConflict, staleEdits } from '../form/conflictMerge';
import { diffForm, isEmptyDiff } from '../form/formDiff';
import { editableSchema, type ProviderFormValues, toFormValues } from '../form/formModel';

interface FormBaseArgs {
    schema: ProviderSchema;
    group: ProviderGroup | null;
    pens: PenRef[];
    providerName: string;
    isOptoScaleAdmin: boolean;
    refetch: () => Promise<LocationProviderConfig | null>;
    form: UseFormReturn<ProviderFormValues>;
    initial: ProviderFormValues;
}

export interface FormBase {
    /** The rows the form's starting values came from, and those values. */
    group: ProviderGroup | null;
    values: ProviderFormValues;
    conflicts: FieldConflict[];
    stale: boolean;
    /** Starts over from saved rows. */
    rebase: (config: LocationProviderConfig) => void;
    /** Refetches and keeps the user's edits on top; false when the refetch gave nothing. */
    mergeFresh: () => Promise<boolean>;
}

interface Base {
    group: ProviderGroup | null;
    values: ProviderFormValues;
}

export function groupOf(
    config: LocationProviderConfig,
    providerName: string
): ProviderGroup | null {
    return config.providers.find((group) => group.providerName === providerName) ?? null;
}

/**
 * What the form is compared with, and how it follows newer rows: without edits the form
 * simply takes them; under edits it only says they arrived (`stale`), and a merge keeps
 * every edit on top of them.
 */
export function useFormBase(args: FormBaseArgs): FormBase {
    const { schema, group, pens, providerName, isOptoScaleAdmin, refetch, form, initial } = args;
    const editable = editableSchema(schema, isOptoScaleAdmin);
    const [base, setBase] = useState<Base>({ group, values: initial });
    const [conflicts, setConflicts] = useState<FieldConflict[]>([]);
    const [stale, setStale] = useState(false);
    // The base the form was last reset to, set in the same step as form.reset. react-hook-form
    // applies a reset at once while setBase waits for a render, and a refetch renders at sync
    // priority (useSyncExternalStore): landing in between, a diff taken in render would pair
    // the saved values with the pre-save base, see edits, and call the user's own save stale.
    const formBase = useRef<Base>(base);
    // By content: a caller handing in a fresh but equal list must not reset the form.
    const pensKey = JSON.stringify(pens);
    const seen = useRef({ group, pensKey });

    function adopt(
        next: Base,
        formValues: ProviderFormValues,
        nextConflicts: FieldConflict[]
    ): void {
        formBase.current = next;
        setBase(next);
        form.reset(formValues);
        setConflicts(nextConflicts);
        setStale(false);
    }

    function startFrom(nextGroup: ProviderGroup | null, nextPens: PenRef[]): void {
        const values = toFormValues(schema, nextGroup, nextPens);
        adopt({ group: nextGroup, values }, values, []);
    }

    /**
     * A pen added to the site under edits joins the form and its base as the base rows show
     * it: the card lists every pen it is handed, and Connect needs the pen's whole entry.
     * Returns the pens added.
     */
    function addArrivedPens(nextPens: PenRef[]): PenRef[] {
        const current = formBase.current;
        const arrived = nextPens.filter((pen) => !(penKey(pen.cageId) in current.values.pens));
        if (arrived.length === 0) return arrived;
        const added = toFormValues(schema, current.group, arrived).pens;
        const next = {
            group: current.group,
            values: { ...current.values, pens: { ...current.values.pens, ...added } },
        };
        formBase.current = next;
        setBase(next);
        for (const [key, pen] of Object.entries(added)) form.setValue(`pens.${key}`, pen);
        return arrived;
    }

    function rowsArrived(nextGroup: ProviderGroup | null, nextPens: PenRef[]): void {
        const current = formBase.current;
        const diff = diffForm(editable, current.values, form.getValues());
        if (isEmptyDiff(diff)) {
            startFrom(nextGroup, nextPens);
            return;
        }
        const arrived = addArrivedPens(nextPens);
        // One that came already connected shows as not connected until the user merges.
        const arrivedConnected = arrived.some((pen) =>
            nextGroup?.rows.some((row) => row.cageId === pen.cageId)
        );
        if (arrivedConnected || staleEdits(current.group, nextGroup, diff)) setStale(true);
    }
    const rowsArrivedRef = useRef(rowsArrived);
    rowsArrivedRef.current = rowsArrived;

    const pensRef = useRef(pens);
    pensRef.current = pens;

    useEffect(() => {
        if (group === seen.current.group && pensKey === seen.current.pensKey) return;
        seen.current = { group, pensKey };
        rowsArrivedRef.current(group, pensRef.current);
    }, [group, pensKey]);

    async function mergeFresh(): Promise<boolean> {
        const oldBase = formBase.current.values;
        const fresh = await refetch();
        if (fresh === null) return false;
        const freshGroup = groupOf(fresh, providerName);
        const freshValues = toFormValues(schema, freshGroup, fresh.pens);
        const merged = mergeAfterConflict(oldBase, freshValues, form.getValues());
        adopt({ group: freshGroup, values: freshValues }, merged.values, merged.conflicts);
        return true;
    }

    return {
        group: base.group,
        values: base.values,
        conflicts,
        stale,
        rebase: (config) => startFrom(groupOf(config, providerName), config.pens),
        mergeFresh,
    };
}
